import { FC, useMemo } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { InlineStats } from '../../inline-stats';
import { ActivePatientsMetricMulti } from '../active-patients-metric-multi';
import { ActivePatientsMetricSingle } from '../active-patients-metric-single';
import { ActivePatientsTable } from '../active-patients-table';
import { usePracticeAnalyticsShallowStore } from '../hooks/use-practice-analytics-store';
import { SummaryMetric } from '../summary-metric';

const query = gql`
  query {
    location {
      activePatients {
        total
      }
      activePatientsScheduled {
        percentage
      }
      patientDemographics {
        buckets {
          label
          total
          subbuckets {
            label
            total
            persons {
              id
              FirstName: firstName
              LastName: lastName
              PMID: pmid
              MobilePhone: mobilePhone
              HomePhone: homePhone
              WorkPhone: workPhone
              Email: email
              PreferredName: preferredName
              Birthdate: birthdate
              Gender: gender
              age
              primaryInsurance
              nextVisit
            }
          }
        }
        details {
          insuranceProviders(top: 5) {
            label
            total
            persons {
              id
              FirstName: firstName
              LastName: lastName
              PMID: pmid
              MobilePhone: mobilePhone
              HomePhone: homePhone
              WorkPhone: workPhone
              Email: email
              PreferredName: preferredName
              Birthdate: birthdate
              Gender: gender
              age
              primaryInsurance
              nextVisit
            }
          }
        }
      }
    }
  }
`;

export const ActivePatientsSubView: FC<React.PropsWithChildren<PracticeAnalyticsTypes.MetricSubViewProps>> = (
  props
) => {
  const { exportEnabled } = props;
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { isSmallMode } = useIsSmallMode();
  const { demoData, filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'demoData',
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );

  const multipleLocationsSelected = (filters.locations?.length || 0) > 1;

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `summaryMetricActivePatientsDemographics-${isDemoAccount}-${JSON.stringify(filters.locations?.sort() || [])}`
    ),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.ActivePatientsDemographicsResponse>({
            locationIds: filters.locations,
            queries: [query],
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.activePatientsDemographics : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const processedData = useMemo(() => {
    const updatedData = PracticeAnalyticsAggregations.activePatientsDetails(data, filters.locations?.length);
    const { activePatients, activePatientsScheduled } = updatedData.aggregatedData || {};

    return {
      data: updatedData,
      quickStats: [
        {
          key: 'activePatients',
          name: isSmallMode ? t('Patients') : t('Active Patients'),
          value: formatters.value(activePatients?.total),
        },
        {
          key: 'scheduledPercentage',
          name: isSmallMode ? t('Schedule %') : t('Scheduled Percentage'),
          value: formatters.percent(activePatientsScheduled?.percentage),
        },
      ],
    };
  }, [data, filters.locations?.length, isSmallMode, t]);

  const Component = multipleLocationsSelected ? ActivePatientsMetricMulti : ActivePatientsMetricSingle;

  return (
    <>
      <InlineStats data={processedData.quickStats} isDemoAccount={showDemoChipAndBanner} isLoading={isLoading} />
      {!multipleLocationsSelected && <SummaryMetric clickNoop hideActivePatientsScheduledMetric isDrillDownPage />}
      {!isSmallMode && <Component {...props} data={processedData.data} isLoading={isLoading} />}
      <ActivePatientsTable data={processedData.data} isExportEnabled={exportEnabled} isLoading={isLoading} />
    </>
  );
};
